import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Directory", href: "#directory" },
    { name: "Apply", href: "#apply" }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (!isHome) {
      // Navigate to home, then scroll to the section after the route mounts
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }, 80);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (!isHome) {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 80);
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header 
      className="glass-header fixed top-0 left-0 right-0 z-50 border-b border-white/10"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="/" 
            onClick={handleLogoClick}
            className="flex items-center group"
            data-testid="logo-link"
          >
            <span className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              <span className="text-white">WTT</span>
              <span className="text-[#D4AF37]">Q</span>
              <span className="text-white">.org</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white/70 hover:text-[#D4AF37] font-medium text-sm tracking-wide transition-colors duration-200"
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-white/70 hover:text-[#D4AF37] font-medium text-sm tracking-wide transition-colors duration-200"
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              )
            ))}
            <Link
              to="/methodology"
              className="inline-flex items-center gap-2 text-[#D4AF37] border border-[#D4AF37]/60 hover:bg-[#D4AF37] hover:text-[#0B132B] px-5 py-2 text-sm font-semibold tracking-wide transition-colors duration-300"
              data-testid="nav-how-it-works"
            >
              How Certification Works
            </Link>
            <a
              href="#apply"
              onClick={(e) => scrollToSection(e, "#apply")}
              className="bg-[#D4AF37] text-[#0B132B] px-6 py-2.5 text-sm font-semibold tracking-wide hover:bg-[#E8C456] transition-colors duration-300"
              data-testid="nav-apply-button"
            >
              Get Certified
            </a>
            {user ? (
              <div className="flex items-center gap-3 pl-4 border-l border-white/20">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-[#D4AF37] transition-colors"
                  data-testid="nav-user-dashboard"
                >
                  <UserIcon className="w-4 h-4" />
                  {user.full_name.split(" ")[0]}
                </Link>
                <button
                  onClick={logout}
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="Logout"
                  data-testid="nav-logout-button"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Link
                to="/signin"
                className="text-white/80 hover:text-[#D4AF37] text-sm font-medium transition-colors border-l border-white/20 pl-6"
                data-testid="nav-signin-link"
              >
                Sign In
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav 
            className="md:hidden py-4 border-t border-white/10"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-white/80 hover:text-[#D4AF37] hover:bg-white/5 px-4 py-3 font-medium text-sm tracking-wide transition-colors duration-200"
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/80 hover:text-[#D4AF37] hover:bg-white/5 px-4 py-3 font-medium text-sm tracking-wide transition-colors duration-200"
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                )
              ))}
              <Link
                to="/methodology"
                onClick={() => setMobileMenuOpen(false)}
                className="mx-4 mt-2 border border-[#D4AF37]/60 text-[#D4AF37] px-6 py-3 text-sm font-semibold tracking-wide text-center hover:bg-[#D4AF37] hover:text-[#0B132B] transition-colors duration-300"
                data-testid="mobile-nav-how-it-works"
              >
                How Certification Works
              </Link>
              <a
                href="#apply"
                onClick={(e) => scrollToSection(e, "#apply")}
                className="mx-4 mt-2 bg-[#D4AF37] text-[#0B132B] px-6 py-3 text-sm font-semibold tracking-wide text-center hover:bg-[#E8C456] transition-colors duration-300"
                data-testid="mobile-nav-apply-button"
              >
                Get Certified
              </a>
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="mx-4 mt-2 border border-white/30 text-white px-6 py-3 text-sm font-medium text-center hover:bg-white hover:text-[#0B132B] transition-colors"
                    data-testid="mobile-nav-dashboard"
                  >
                    My Dashboard ({user.full_name.split(" ")[0]})
                  </Link>
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="mx-4 mt-2 text-white/60 text-sm py-2 text-center hover:text-white"
                    data-testid="mobile-nav-logout"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/signin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mx-4 mt-2 border border-white/30 text-white px-6 py-3 text-sm font-medium text-center hover:bg-white hover:text-[#0B132B] transition-colors"
                  data-testid="mobile-nav-signin"
                >
                  Sign In
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
